import React, { useState, useEffect, useMemo, useCallback } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import { ChatCircle } from "@phosphor-icons/react/dist/ssr/ChatCircle";
import { useGetLastSeenPostQuery, useUpdateLastSeenPostMutation } from "../../../../graphql/generated/graphql";

interface Author {
  id: string;
  name: string;
}

interface Commenter {
  id: string;
  name: string;
}

interface LatestComment {
  author: Commenter;
  timestamp: string;
}

interface Post {
  id: string;
  created: string;
  depth: number;
}

interface ThreadCardProps {
  id: string;
  title: string;
  message: string;
  author: Author;
  responseCount: number;
  commenters: Commenter[];
  latestComment: LatestComment;
  posts?: Post[];
  onPress: (threadId: string) => void;
}

const ThreadCard: React.FC<ThreadCardProps> = ({
  id,
  title,
  message,
  author,
  responseCount,
  commenters,
  latestComment,
  posts = [],
  onPress,
}) => {
  const [hasNewPosts, setHasNewPosts] = useState(false);
  const [updateLastSeenPostMutation] = useUpdateLastSeenPostMutation();

  const {
    data: lastSeenData,
    loading: lastSeenLoading,
    refetch: refetchLastSeen,
  } = useGetLastSeenPostQuery({
    variables: { threadId: id },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  // Refetch last seen data when the component becomes visible again
  useEffect(() => {
    const handleFocus = () => {
      refetchLastSeen();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [refetchLastSeen]);

  useEffect(() => {
    if (!lastSeenLoading && lastSeenData?.getLastSeenPost?.data) {
      const lastSeenPostId = lastSeenData.getLastSeenPost.data.lastSeenPostId;
      const lastSeenAt = lastSeenData.getLastSeenPost.data.lastSeenAt;

      if (!lastSeenPostId || !lastSeenAt) {
        // User has never viewed this thread, mark as new if there are posts
        setHasNewPosts(posts.length > 0);
        return;
      }

      // Check if there are posts newer than the last seen post
      const lastSeenTime = new Date(lastSeenAt).getTime();
      const hasNewerPosts = posts.some((post) => {
        const postTime = new Date(post.created).getTime();
        return postTime > lastSeenTime;
      });

      setHasNewPosts(hasNewerPosts);
    } else if (!lastSeenLoading && !lastSeenData?.getLastSeenPost?.data) {
      setHasNewPosts(posts.length > 0);
    }
  }, [lastSeenData, lastSeenLoading, posts]);

  const handlePress = async () => {
    onPress(id);
  };

  const getStudentInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const commentTime = new Date(timestamp);
    const diffInHours = Math.floor((now.getTime() - commentTime.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return "just now";
    } else if (diffInHours < 24) {
      return `about ${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    }
  };

  const stripHtmlTags = useCallback((html: string) => {
    if (!html) return "";
    const withoutTags = html.replace(/<[^>]*>/g, "");
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = withoutTags;
    return tempDiv.textContent || tempDiv.innerText || "";
  }, []);

  const threadContent = useMemo(() => (message ? stripHtmlTags(message) : ""), [message, stripHtmlTags]);

  const renderCommenterAvatars = () => {
    const maxVisible = 5;
    const visibleCommenters = commenters.slice(0, maxVisible);
    const remainingCount = Math.max(0, commenters.length - maxVisible);

    return (
      <View style={styles.avatarsContainer}>
        {visibleCommenters.map((commenter, index) => (
          <View
            key={commenter.id}
            style={[styles.commenterAvatar, { marginLeft: index > 0 ? -8 : 0, zIndex: maxVisible - index }]}>
            <CustomText
              text={getStudentInitial(commenter.name)}
              size="xs"
              weight="bold"
              style={styles.commenterAvatarText}
              useTranslationText={false}
            />
          </View>
        ))}
        {remainingCount > 0 && (
          <View style={[styles.commenterAvatar, { marginLeft: -8, backgroundColor: "#E5E7EB" }]}>
            <CustomText
              text={`+${remainingCount}`}
              size="xs"
              weight="bold"
              style={[styles.commenterAvatarText, { color: "#6B7280" }]}
              useTranslationText={false}
            />
          </View>
        )}
      </View>
    );
  };

  return (
    <TouchableOpacity style={styles.container} onPress={handlePress} activeOpacity={0.7}>
      <View style={styles.mainContent}>
        {/* Left side - Main content (3/4 width) */}
        <View style={styles.leftContent}>
          <View>
            <View style={styles.authorAvatar}>
              <CustomText
                text={getStudentInitial(author.name)}
                size="s"
                weight="bold"
                style={styles.avatarText}
                useTranslationText={false}
              />
            </View>
          </View>
          <View style={styles.contentColumn}>
            <View style={styles.headerContent}>
              <View style={styles.titleRow}>
                {hasNewPosts && (
                  <View style={styles.newBadge}>
                    <CustomText
                      text="New"
                      size="xs"
                      weight="ultraBold"
                      style={styles.newBadgeText}
                      useTranslationText={false}
                    />
                  </View>
                )}
                <CustomText
                  text={title}
                  size="m"
                  weight="bold"
                  style={[styles.title, hasNewPosts && styles.titleWithBadge]}
                  useTranslationText={false}
                  numberOfLines={1}
                  ellipsizeMode={"tail"}
                />
              </View>
              <View style={styles.authorInfo}>
                <CustomText text={`by ${author.name}`} size="s" style={styles.authorName} useTranslationText={false} />
                <View style={styles.commentCount}>
                  <ChatCircle size={16} color="#6B7280" weight="regular" />
                  <CustomText
                    text={responseCount.toString()}
                    size="s"
                    style={styles.commentCountText}
                    useTranslationText={false}
                  />
                </View>
              </View>
            </View>
            <CustomSpacing size="m" type="vertical" />
            <View style={styles.messageContainer}>
              <CustomText
                text={threadContent}
                style={styles.message}
                useTranslationText={false}
                numberOfLines={1}
                ellipsizeMode={"tail"}
              />
            </View>
          </View>
        </View>

        {/* Right side - Avatars and latest comment (1/4 width) */}
        <View style={styles.rightContent}>
          {renderCommenterAvatars()}

          <View style={styles.latestCommentSection}>
            <CustomText
              text={`latest comment by ${latestComment.author.name} ${formatTimeAgo(latestComment.timestamp)}`}
              size="xs"
              style={styles.latestCommentText}
              useTranslationText={false}
            />
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 32,
    paddingTop: 32,
  },
  mainContent: {
    flexDirection: "row",
  },
  leftContent: {
    flex: 3,
    paddingRight: 16,
    flexDirection: "row",
  },
  contentColumn: {
    flexDirection: "column",
    flex: 1,
  },
  rightContent: {
    flex: 1,
    justifyContent: "flex-start",
    paddingTop: 16,
  },
  authorAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  avatarText: {
    color: "#FFFFFF",
    fontSize: 14,
  },
  headerContent: {},
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  newBadge: {
    backgroundColor: "#0084FF",
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 10,
    flexShrink: 0,
  },
  newBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
  },
  title: {
    color: "#101828",
    flex: 1,
  },
  titleWithBadge: {
    flex: 1,
  },
  authorInfo: {
    flexDirection: "column",
  },
  authorName: {
    color: "#6B7280",
  },
  commentCount: {
    flexDirection: "row",
    alignItems: "center",
  },
  commentCountText: {
    color: "#6B7280",
  },
  messageContainer: {
    marginBottom: 16,
  },
  message: {
    color: "#667085",
    lineHeight: 20,
    flexWrap: "wrap",
    fontSize: 14,
  },
  avatarsContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: 16,
  },
  commenterAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: "#FFFFFF",
  },
  commenterAvatarText: {
    color: "#FFFFFF",
    fontSize: 10,
  },
  latestCommentSection: {
    justifyContent: "flex-start",
  },
  latestCommentText: {
    color: "#6B7280",
    lineHeight: 16,
  },
});

export default ThreadCard;
